import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useSwot } from '../context/SwotContext';

const { FiEdit2, FiTrash2, FiCheck, FiX, FiMove } = FiIcons;

const SwotItem = ({ item, category, color }) => {
  const { updateItem, deleteItem } = useSwot();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(item.text);
  const [editPriority, setEditPriority] = useState(item.priority);
  const [isDragging, setIsDragging] = useState(false);

  const priorityColors = {
    high: 'bg-red-100 text-red-800 border-red-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-green-100 text-green-800 border-green-200'
  };

  const handleSave = () => {
    if (editText.trim()) {
      updateItem(category, item.id, editText, editPriority);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditText(item.text);
    setEditPriority(item.priority);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('application/json', JSON.stringify({
      itemId: item.id,
      sourceCategory: category,
      itemData: item
    }));
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    e.target.style.opacity = '1';
  };

  if (isEditing) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-4 shadow-md border-2 border-blue-200 mb-3"
      >
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onKeyPress={handleKeyPress}
          className="w-full p-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          rows={3}
          autoFocus
        />
        <div className="flex items-center justify-between mt-3">
          <select
            value={editPriority}
            onChange={(e) => setEditPriority(e.target.value)}
            className="px-2 py-1 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
            >
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: isDragging ? 0.5 : 1, 
        y: 0,
        scale: isDragging ? 0.95 : 1
      }}
      whileHover={{ scale: 1.02 }}
      className={`bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all duration-200 mb-3 cursor-move border-l-4 ${
        color === 'green' ? 'border-l-green-500' :
        color === 'red' ? 'border-l-red-500' :
        color === 'blue' ? 'border-l-blue-500' :
        'border-l-orange-500'
      } ${isDragging ? 'rotate-2 shadow-2xl' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-gray-800 text-sm leading-relaxed">{item.text}</p>
          <div className="flex items-center justify-between mt-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${priorityColors[item.priority]}`}>
              {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
            </span>
            <div className="flex items-center gap-1">
              <SafeIcon icon={FiMove} className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-400">Drag to move</span>
            </div>
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setIsEditing(true)}
            className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
          >
            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
          </button>
          <button
            onClick={() => deleteItem(category, item.id)}
            className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
          >
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default SwotItem;